import dayjs from 'dayjs';

import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(timezone);
import weekOfYear from 'dayjs/plugin/weekOfYear';
dayjs.extend(weekOfYear);
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
dayjs.extend(quarterOfYear);

// Types
import type { DatepickerPeriodsAvailableType } from '@/lib/periods';
import type { Dayjs } from 'dayjs';

type GetDatePeriodsParamsType = {
    minDateTz: Dayjs;
    currentDayTz: Dayjs;
    period: DatepickerPeriodsAvailableType;
};

export const getDatePeriods = ({
    minDateTz,
    period,
    currentDayTz
}: GetDatePeriodsParamsType): { from: Dayjs; to: Dayjs } => {
    switch (period) {
        case 'all_time':
            return {
                from: minDateTz ?? currentDayTz.subtract(29, 'day').startOf('day'),
                to: currentDayTz
            };
        case 'previous_week':
            return {
                from: currentDayTz.subtract(1, 'week').startOf('week'),
                to: currentDayTz.subtract(1, 'week').endOf('week')
            };
        case 'this_week':
            return { from: currentDayTz.startOf('week'), to: currentDayTz };

        case 'last_7_days':
            return {
                from: currentDayTz.startOf('day').subtract(6, 'days'),
                to: currentDayTz.endOf('day')
            };
        case 'last_day':
            return {
                from: currentDayTz.startOf('day').subtract(1, 'day'),
                to: currentDayTz.subtract(1, 'day').endOf('day')
            };

        case 'previous_month':
            return {
                from: currentDayTz.subtract(1, 'month').startOf('month'),
                to: currentDayTz.subtract(1, 'month').endOf('month')
            };

        case 'this_month':
            return { from: currentDayTz.startOf('month'), to: currentDayTz };
        case 'last_30_days':
            return {
                from: currentDayTz.subtract(29, 'day').startOf('day'),
                to: currentDayTz.endOf('day')
            };
        case 'last_3_months':
            return {
                from: currentDayTz.subtract(3, 'month').startOf('day'),
                to: currentDayTz.endOf('day')
            };

        case 'this_year':
            return { from: currentDayTz.startOf('year'), to: currentDayTz };

        case 'previous_year':
            return {
                from: currentDayTz.subtract(1, 'year').startOf('year'),
                to: currentDayTz.subtract(1, 'year').endOf('year')
            };

        case 'this_quarter':
            return { from: currentDayTz.startOf('quarter'), to: currentDayTz };

        case 'previous_quarter':
            return {
                from: currentDayTz.subtract(1, 'quarter').startOf('quarter'),
                to: currentDayTz.subtract(1, 'quarter').endOf('quarter')
            };
    }
};

type CheckDateWithMinMaxParamsType = {
    currentDate: Dayjs;
    minDate: string;
    maxDate: string;
};

export const checkDateWithMinMax = ({
    currentDate,
    minDate,
    maxDate
}: CheckDateWithMinMaxParamsType) => {
    if (maxDate && dayjs(maxDate).endOf('day').isBefore(currentDate)) {
        // If there is a maxDate => check if the end date is greater than maxDate
        return dayjs(maxDate).endOf('day');
    }

    if (minDate && dayjs(minDate).startOf('day').isAfter(currentDate)) {
        // If there is a minDate => check if the end date is lower than minDate
        return dayjs(minDate).startOf('day');
    }

    return currentDate;
};
